import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildSessionEvidenceUrl, connectSessionEvidenceStream } from 'utils/sessionEvidence.utils';
import { IDeviceSyncCompleteReq } from 'types/wms/device-sync.types';
import { IToolInspectionImageReq } from 'types/suscription/toolInspection.types';

export const EVIDENCE_PHASE = {
  Qr: 'qr',
  PhoneActive: 'phoneActive',
  Gallery: 'gallery'
} as const;

export type TInspectionEvidencePhase = typeof EVIDENCE_PHASE[keyof typeof EVIDENCE_PHASE];

interface IUseInspectionSessionEvidenceOptions {
  images: IToolInspectionImageReq[] | null;
  productId: number;
  enabled?: boolean;
}

/**
 * Hook para manejar la sesión de evidencias por QR/SSE con dispositivos secundarios.
 */
export const useInspectionSessionEvidence = ({
  images,
  productId,
  enabled = false
}: IUseInspectionSessionEvidenceOptions) => {
  const [sessionId, setSessionId] = useState(() => uuidv4());
  const [uiPhase, setUiPhase] = useState<TInspectionEvidencePhase>(EVIDENCE_PHASE.Qr);
  const [sseCompletedEvidence, setSseCompletedEvidence] = useState<IDeviceSyncCompleteReq | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [isStreamReady, setIsStreamReady] = useState(false);

  const evidenceUrlForQr = useMemo(() => buildSessionEvidenceUrl(sessionId, { productId }), [sessionId, productId]);

  const hasImageUrls = useMemo(() => Boolean(images?.some(img => img.url)), [images]);
  const isUiPhase = uiPhase === EVIDENCE_PHASE.Qr;
  const showEvidenceGallery = isUiPhase && hasImageUrls && !enabled;

  const resetSession = useCallback(() => {
    setIsStreamReady(false);
    setSessionId(uuidv4());
    setSseCompletedEvidence(null);
    setUiPhase(EVIDENCE_PHASE.Qr);
    setStreamError(null);
  }, []);

  const confirmTransferredPhotos = useCallback(() => {
    if (!sseCompletedEvidence) return;

    setSseCompletedEvidence(null);
    setUiPhase(EVIDENCE_PHASE.Gallery);
  }, [sseCompletedEvidence]);

  useEffect(() => {
    if (!enabled) {
      setIsStreamReady(false);
      return;
    }

    setIsStreamReady(false);

    return connectSessionEvidenceStream(sessionId, {
      setStreamError,
      setUiPhase,
      setSseCompletedEvidence,
      setStreamReady: setIsStreamReady
    });
  }, [sessionId, enabled]);

  return {
    sessionId,
    evidenceUrlForQr,
    uiPhase: showEvidenceGallery ? EVIDENCE_PHASE.Gallery : uiPhase,
    sseCompletedEvidence,
    isStreamReady,
    streamError,
    confirmTransferredPhotos,
    rescanQr: resetSession,
    changeUploadedPhotos: resetSession
  };
};

export type UseInspectionSessionEvidenceResult = ReturnType<typeof useInspectionSessionEvidence>;
